import BaseInnerWrapper from "../components/ui/BaseInnerWrapper";
import BaseWrapper from "../components/ui/BaseWrapper";
import BrandLogo from "../assets/sidebar-nav/brand-logo.svg";
import BaseRadio from "../components/ui/BaseRadio";
import { RadioGroup } from "@mui/material";
import { brandPrimaryColor } from "../utils/variables";
import { useEffect, useState } from "react";
import BaseButton from "../components/ui/BaseButton";
// Social sign-up temporarily disabled.
// import GoogleSignInButton from "../components/ui/GoogleSignInButton";
// import AppleSignInButton from "../components/ui/AppleSignInButton";
import useSignupTicket from "../hooks/useSignupTicket";
import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";

const GetStartedPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [signupType, setSignupType] = useState("");

  // Someone who signed in with Google but has no account yet arrives here holding a
  // ticket — Google has vouched for them, we just don't know what kind of account
  // they want. The hook captures it on the first render, before the effect below
  // rewrites the query string to add ?signup=... and would otherwise drop it.
  const { ticket, prefill, isExpired, isSocial } = useSignupTicket();

  // This page rewrites its own query string below, which would otherwise drop the
  // ticket and cost the user a second trip to Google if they reloaded.
  const ticketParam = ticket ? `&ticket=${encodeURIComponent(ticket)}` : "";

  useEffect(() => {
    const signupParam = new URLSearchParams(location.search).get("signup");

    if (signupParam) {
      setSignupType(signupParam);
    }
  }, [location.search]);

  const selectSignupType = (value) => {
    if (value === signupType) return;
    setSignupType(value);
    navigate(`?signup=${value}${ticketParam}`);
  };

  const cardClassName = (value) =>
    `md:col-span-1 col-span-2 py-6 pl-6 border-2 rounded-lg cursor-pointer transition-colors ${
      signupType === value ? "border-primary bg-[#EEF3FC]" : "border-zinc-200 bg-white"
    }`;

  const getStartedButtonClicked = () => {
    // Carry the ticket to whichever form they pick, so they don't have to go back to
    // Google a second time.
    const query = ticket ? `?ticket=${encodeURIComponent(ticket)}` : "";

    navigate(signupType === "TECH" ? `/techSignup${query}` : `/customerSignup${query}`);
  };
  return (
    <BaseWrapper>
      <BaseInnerWrapper className="min-h-screen flex flex-col lg:py-[clamp(16px,3vh,64px)] py-8 mx-0 px-[0px]">
        <div className="flex-1 lg:border border-zinc-300 rounded-lg  lg:py-[clamp(32px,5vh,96px)] lg:px-10  flex flex-col items-center">
          <div className=" flex flex-col justify-center items-center cursor-default">
            <img src={BrandLogo} width={110} />
            <p className="text-xl text-[#525354]  font-normal mt-12 lg:mt-[clamp(24px,4.5vh,72px)]">Join Tech Domain</p>
            <h1
              className={`lg:text-5xl text-3xl text-[#525354] text-center mt-6 lg:leading-[70px] ${
                isSocial ? "mb-10" : "mb-4"
              }`}
            >
              {isSocial
                ? "One last step before we set you up"
                : "Connecting Businesses with Skilled Field Technicians—When and Where They’re Needed"}
            </h1>
            {!isSocial && (
              <p className="text-xl text-[#525354] font-normal text-center mb-16 lg:mb-[clamp(32px,7vh,112px)]">
                Connect with opportunities or skilled field technicians—all in one platform
              </p>
            )}

            {/* Google has already vouched for them — this is a step forward in the
                flow, not a failure, so it reads as confirmation rather than an error. */}
            {isSocial && !isExpired && (
              <div className="w-full bg-light border border-zinc-300 rounded-lg px-8 py-6 mb-12">
                <div className="flex items-start">
                  <i className="fa-solid fa-check text-primary bg-[#D8EBF8] rounded-full flex items-center justify-center max-h-[28px] min-w-[28px] mt-[2px]"></i>
                  <div className="ml-4">
                    <p className="text-[#525354] font-semibold text-base mb-1">
                      Signed in with Google
                      {prefill?.email ? (
                        <>
                          {" "}
                          as <span className="text-primary">{prefill.email}</span>
                        </>
                      ) : null}
                    </p>
                    <p className="text-dark text-base">
                      Tell us how you’ll be using Tech Domain, then we’ll just need a few details to
                      set one up.{" "}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isExpired && (
              <div className="w-full border border-red-300 bg-red-50 rounded-lg px-8 py-6 mb-12">
                <p className="text-dark text-base text-center">
                  Your Google sign-in link has expired. Please{" "}
                  <Link className="text-primary underline underline-offset-4" to="/login">
                    sign in again
                  </Link>
                  .
                </p>
              </div>
            )}
          </div>

          <RadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            value={signupType}
            name="radio-buttons-group"
            className="w-full"
            onChange={(e) => selectSignupType(e.target.value)}
          >
            <div className="grid grid-cols-2 gap-8 mt-6">
              <div className={cardClassName("TECH")} onClick={() => selectSignupType("TECH")}>
                <BaseRadio
                  color={brandPrimaryColor}
                  fontSize="30px"
                  label={<h3 className="font-semibold text-[#525354] whitespace-nowrap text-lg lg:text-xl xl:text-2xl 2xl:text-[26px]">Looking for Work</h3>}
                  value="TECH"
                  name="signupType"
                  className="pointer-events-none"
                />
                <p className="text-dark text-[15px] italic mt-2">For technicians & Service Providers/Vendors</p>
                <p className="text-[#525354] text-[15px] font-semibold mb-5 mt-4">
                  Find work. Build connections. Grow your business.
                </p>
                <KeyBenefits text="Choose jobs that match your skills and availability" />
                <KeyBenefits text="Connect with businesses looking for field expertise" />
                <KeyBenefits text="Manage jobs and track earnings in one place" />
              </div>
              <div className={cardClassName("CLIENT")} onClick={() => selectSignupType("CLIENT")}>
                <BaseRadio
                  color={brandPrimaryColor}
                  fontSize="30px"
                  label={<h3 className="font-semibold text-[#525354] whitespace-nowrap text-lg lg:text-xl xl:text-2xl 2xl:text-[26px]">Looking for Technicians</h3>}
                  value="CLIENT"
                  name="signupType"
                  className="pointer-events-none"
                />
                <p className="text-dark text-[15px] italic mt-2">For businesses & service organisations</p>
                <p className="text-[#525354] text-[15px] font-semibold mb-5 mt-4">
                  Find skilled technicians when and where you need them.
                </p>
                <KeyBenefits text="Access qualified field technicians across locations" />
                <KeyBenefits text="Compare skills, experience and rates" />
                <KeyBenefits text="Manage work orders from dispatch through completion" />
              </div>
            </div>
          </RadioGroup>

          <BaseButton
            color="primary"
            className={`py-[8px] px-[40px] text-base rounded-full ${isSocial ? "mt-12" : "mt-16"} lg:mt-[clamp(32px,7vh,112px)]`}
            onClick={getStartedButtonClicked}
            disabled={isExpired || !signupType}
          >
            {isSocial ? "Continue" : "Get Started"}
          </BaseButton>

          {/* Hidden once they already hold a ticket — they've authenticated, they
              just need to pick which kind of account to finish setting up. */}
          {/* Social sign-up temporarily disabled.
          {!isSocial && (
            <div className="w-full max-w-[380px] mt-8">
              <p className="separator text-base">
                <span className="line"></span>
                <span className="or-text"> or </span>
                <span className="line"></span>
              </p>
              <GoogleSignInButton
                label="Sign up with Google"
                intent="signup"
                role={signupType}
                className="mt-6"
              />
              <AppleSignInButton
                label="Sign up with Apple"
                intent="signup"
                role={signupType}
                className="mt-4"
              />
            </div>
          )}
          */}

          <p className="text-[#525354]  text-base mt-8 lg:mt-[clamp(24px,4vh,64px)]">
            Already have an account?{" "}
            <Link
              className="text-base underline underline-offset-[6px] text-primary font-normal "
              to="/login"
            >
              Login
            </Link>
          </p>
        </div>
      </BaseInnerWrapper>
    </BaseWrapper>
  );
};

export default GetStartedPage;

const KeyBenefits = ({ text }) => {
  return (
    <div className="flex">
      <i className="fa-solid fa-check text-primary bg-[#D8EBF8] rounded-full flex items-center justify-center max-h-[28px] min-w-[28px]"></i>
      <p className="text-dark text-[15px] ml-4 mb-3 pr-12">{text}</p>
    </div>
  );
};
